import React from "react";
import { Link } from "react-router-dom";
import { useAuth } from "../contexts/AuthContext";
import {
  ArrowLeft,
  ChevronRight,
  Clock,
  ThumbsUp,
  Share2,
  CheckCircle2,
  UserCheck,
  Layout,
  Settings,
  Phone,
  MessageCircle,
  MapPin
} from "lucide-react";

export default function ConfiguracoesTutorialPage() {
  const { user } = useAuth();
  
  return (
    <div className="min-h-screen bg-slate-50 text-gray-900 font-sans pb-20">
      <header className="border-b bg-white sticky top-0 z-40 shadow-sm">
        <div className="max-w-4xl mx-auto px-4 py-3 flex flex-col md:flex-row justify-between items-center gap-4">
          <Link to="/">
            <img src="/marketing/logo_official.png" className="h-28 object-contain" alt="OdontoAssist AI Logo" />
          </Link>
          <div className="flex items-center gap-6">
            <Link to="/ajuda/tutoriais" className="text-gray-500 hover:text-indigo-600 font-semibold hidden md:block">
              Central de Guias
            </Link>
            <Link 
              to="/" 
              className="bg-indigo-600 hover:bg-indigo-700 text-white px-6 py-3 rounded-2xl font-black tracking-wide flex items-center gap-2 transition-all shadow-lg hover:-translate-y-1"
            >
              <ArrowLeft size={20} />
              Voltar {user ? "para o Aplicativo" : "ao Início"}
            </Link>
          </div>
        </div>
      </header>

      <main className="max-w-4xl mx-auto px-4 py-10">
        {/* Breadcrumb */}
        <div className="flex items-center gap-2 text-sm font-semibold text-gray-500 mb-6">
          <Link to="/ajuda" className="hover:text-indigo-600">Ajuda</Link>
          <ChevronRight size={14} className="text-gray-400" />
          <Link to="/ajuda/tutoriais" className="hover:text-indigo-600">Guias</Link>
          <ChevronRight size={14} className="text-gray-400" />
          <span className="text-indigo-600">Gestão e Agenda</span>
        </div>

        <h1 className="text-3xl md:text-4xl font-black text-gray-900 mb-4 leading-tight">
          Configurações Iniciais da Clínica
        </h1>
        
        <div className="flex flex-wrap items-center gap-6 text-sm text-gray-500 mb-8">
          <div className="flex items-center gap-2">
            <Clock size={16} /> <span>5 min de leitura / vídeo</span>
          </div>
          <div className="flex items-center gap-2 bg-indigo-50 text-indigo-700 px-3 py-1 rounded-full font-bold uppercase tracking-wider text-xs">
            Gestão e Agenda
          </div>
        </div>

        {/* Video Player */}
        <div className="w-full aspect-video rounded-3xl overflow-hidden relative shadow-2xl mb-12 border-4 border-white bg-black">
          <iframe 
            width="100%" 
            height="100%" 
            src="https://www.youtube.com/embed/8ojYdsIU0rU" 
            title="Configurações iniciais do OdontoAssist AI" 
            frameBorder="0" 
            allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share" 
            referrerPolicy="strict-origin-when-cross-origin" 
            allowFullScreen
            className="absolute top-0 left-0 w-full h-full"
          ></iframe>
        </div>

        <div className="flex items-center justify-between border-b border-gray-200 pb-8 mb-10">
          <div className="flex items-center gap-4">
            <img src="https://i.pravatar.cc/150?img=32" alt="Instrutora" className="w-12 h-12 rounded-full border-2 border-white shadow-sm" />
            <div>
              <p className="font-bold text-gray-900">Dra. Camila Marques</p>
              <p className="text-sm text-gray-500">Especialista em Sucesso do Cliente</p>
            </div>
          </div>
          <div className="flex gap-3">
            <button className="flex items-center gap-2 px-4 py-2 rounded-xl border border-gray-200 text-gray-600 font-semibold hover:bg-gray-50 transition-colors">
              <ThumbsUp size={18} /> Útil
            </button>
            <button className="flex items-center gap-2 px-4 py-2 rounded-xl border border-gray-200 text-gray-600 font-semibold hover:bg-gray-50 transition-colors">
              <Share2 size={18} /> Compartilhar
            </button>
          </div>
        </div>

        {/* Written Tutorial */}
        <article className="prose prose-indigo prose-lg max-w-none text-gray-600 text-justify">
          <p className="lead text-xl text-gray-700 font-medium">
            Seja muito bem-vindo ao OdontoAssist AI! O primeiro passo antes de agendar pacientes ou emitir relatórios é <strong>configurar as bases da sua conta</strong>. Nesta etapa de fundação garantimos que sua marca, dados legais e horários sejam aplicados em toda a plataforma.
          </p>

          <div className="bg-indigo-50 border border-indigo-200 rounded-3xl p-6 md:p-8 mt-8">
            <h2 className="text-2xl font-bold text-indigo-950 mt-0 mb-4">Como funciona o onboarding progressivo</h2>

            <p className="text-indigo-900">
              No primeiro acesso, ative o período gratuito na página de <strong>Assinatura</strong>. Em seguida, o OdontoAssist AI orientará você a preparar a clínica antes de começar a usar os demais módulos.
            </p>

            <ol className="mt-5 space-y-3 text-indigo-900">
              <li><strong>Configure sua clínica:</strong> preencha dados básicos, profissionais, especialidades, documentos e horários.</li>
              <li><strong>Comece a rotina:</strong> cadastre o primeiro paciente, agende uma consulta e conclua uma anamnese.</li>
              <li><strong>Conheça os recursos:</strong> gere um documento, experimente a IA contextual e registre a primeira movimentação financeira.</li>
            </ol>

            <p className="text-indigo-900 mt-5 mb-0">
              O botão <strong>Primeiros passos</strong> mostra seu progresso. Use <strong>Me mostrar</strong> para iniciar um tour guiado; depois de concluir uma tarefa, a opção <strong>Rever como fazer</strong> continuará disponível. As dicas contextuais podem ser adiadas por três dias ou desativadas. Ao finalizar todas as etapas, o painel passa a se chamar <strong>Guia rápido</strong> e permanece acessível para consultas futuras.
            </p>

            <div className="mt-5 bg-white/80 border border-indigo-100 rounded-2xl p-4">
              <p className="text-sm text-indigo-800 mb-0">
                <strong>Observação:</strong> o onboarding inicial é destinado ao responsável pela conta. Usuários convidados, como profissionais e secretárias, acessam diretamente os recursos liberados conforme suas permissões.
              </p>
            </div>
          </div>

          <h2 className="text-2xl font-bold text-gray-900 mt-10 mb-4">Passo a Passo em Texto</h2>
          
          <div className="space-y-8 mt-8">
            <div className="bg-white p-8 rounded-3xl border border-gray-100 shadow-sm relative overflow-hidden">
               <div className="absolute top-0 left-0 w-2 h-full bg-indigo-500" />
               <h3 className="text-xl font-bold text-gray-900 mb-3 flex items-center gap-3">
                 <span className="w-8 h-8 rounded-full bg-indigo-100 text-indigo-700 flex items-center justify-center text-sm font-black">1</span>
                 Acessando o Painel de Configurações
               </h3>
               <p>No menu lateral esquerdo do seu painel, deslize até a opção de <strong>Configurações</strong>.</p>
               <p>A tela exibirá diversos blocos fundamentais de gestão da clínica.</p>
            </div>

            <div className="bg-white p-8 rounded-3xl border border-gray-100 shadow-sm relative overflow-hidden">
               <div className="absolute top-0 left-0 w-2 h-full bg-indigo-500" />
               <h3 className="text-xl font-bold text-gray-900 mb-3 flex items-center gap-3">
                 <span className="w-8 h-8 rounded-full bg-indigo-100 text-indigo-700 flex items-center justify-center text-sm font-black">2</span>
                 Preenchimento de Dados e Profissionais
               </h3>
               <ul className="mt-2 list-none space-y-4">
                  <li className="flex items-start gap-3">
                     <UserCheck className="shrink-0 text-indigo-500 mt-1" size={20} />
                     <div>
                       <strong>Dados Básicos e Profissionais:</strong><br/>
                       Defina o Nome da Clínica, Endereço e gerencie a lista de cirurgiões-dentistas da equipe. Ao adicionar um profissional, não esqueça de colocar o seu CRO oficial e especialidade. Esses dados sairão automaticamente nos rodapés dos seus receituários e contratos.
                     </div>
                  </li>
                  <li className="flex items-start gap-3">
                     <Settings className="shrink-0 text-indigo-500 mt-1" size={20} />
                     <div>
                       <strong>Documentos Legais:</strong><br/>
                       Mantenha o CNPJ ou CPF do Responsável Técnico atualizado para questões administrativas em contratos de prestação de serviços odontológicos e pagamentos.
                     </div>
                  </li>
               </ul>
            </div>

            <div className="bg-white p-8 rounded-3xl border border-gray-100 shadow-sm relative overflow-hidden">
               <div className="absolute top-0 left-0 w-2 h-full bg-indigo-500" />
               <h3 className="text-xl font-bold text-gray-900 mb-3 flex items-center gap-3">
                 <span className="w-8 h-8 rounded-full bg-indigo-100 text-indigo-700 flex items-center justify-center text-sm font-black">3</span>
                 Horários e Especialidades
               </h3>
               <p>Navegue pela seção de <strong>Especialidades Oferecidas</strong> (ex: Implantodontia, Ortodontia, Harmonização) para facilitar a segmentação dos seus serviços na agenda.</p>
               <p>Abaixo, em <strong>Horário de Funcionamento</strong>, configure os dias e turnos padrão em que a clínica está aberta. Isso evita agendamentos em dias em que o consultório não opera.</p>
            </div>

            <div className="bg-white p-8 rounded-3xl border border-gray-100 shadow-sm relative overflow-hidden">
               <div className="absolute top-0 left-0 w-2 h-full bg-indigo-500" />
               <h3 className="text-xl font-bold text-gray-900 mb-3 flex items-center gap-3">
                 <span className="w-8 h-8 rounded-full bg-indigo-100 text-indigo-700 flex items-center justify-center text-sm font-black">4</span>
                 Visualização e Identidade Visual Centralizada
               </h3>
               <div className="flex flex-col md:flex-row gap-6 mt-4">
                 <div className="flex-1">
                   <p>A seção <strong>Visual da Plataforma</strong> é o diferencial estratégico! Envie sua <strong>Logo</strong> em alta qualidade (.jpg ou .png).</p>
                   <p className="mt-2">Logo que a marca da clínica foi enviada e confirmada, salve toda a página de configurações. A partir daqui, quase tudo no ecossistema (relatórios gerados, PDFs assináveis, cabeçalhos das anamneses) irá, por padrão, apresentar a logomarca e endereço que você acabou de salvar!</p>
                 </div>
                 <div className="bg-indigo-50 p-6 rounded-2xl flex items-center justify-center gap-3 w-full md:w-48 shrink-0">
                   <Layout className="text-indigo-600" size={32} />
                   <span className="text-xs font-bold uppercase text-indigo-800 text-center">Identidade<br/>Automatizada</span>
                 </div>
               </div>
            </div>

            <div className="bg-white p-8 rounded-3xl border border-gray-100 shadow-sm relative overflow-hidden">
               <div className="absolute top-0 left-0 w-2 h-full bg-green-500" />
               <h3 className="text-xl font-bold text-gray-900 mb-3 flex items-center gap-3">
                 <span className="w-8 h-8 rounded-full bg-green-100 text-green-700 flex items-center justify-center text-sm font-black">5</span>
                 Comunicação da Clínica
               </h3>

               <p>
                 Na aba <strong>Dados Básicos</strong>, mantenha atualizados os contatos oficiais da clínica. Essas informações são usadas em mensagens automáticas, confirmações de consulta, lembretes, mensagens de aniversário, autoagendamento e outras comunicações enviadas pelo OdontoAssist AI.
               </p>

               <div className="grid md:grid-cols-3 gap-4 mt-5">
                 <div className="bg-green-50 border border-green-100 rounded-2xl p-4">
                   <MessageCircle className="text-green-600 mb-2" size={24} />
                   <h4 className="font-bold text-green-900 mb-1">WhatsApp da Clínica</h4>
                   <p className="text-sm text-green-800">
                     Informe o número que o paciente deve usar para falar diretamente com a clínica. Ele aparece nas mensagens enviadas pelo módulo WhatsApp Business.
                   </p>
                 </div>

                 <div className="bg-slate-50 border border-slate-100 rounded-2xl p-4">
                   <Phone className="text-slate-600 mb-2" size={24} />
                   <h4 className="font-bold text-slate-900 mb-1">Telefone da Clínica</h4>
                   <p className="text-sm text-slate-700">
                     Use um telefone de contato alternativo, como fixo, celular institucional ou outro canal usado pela recepção.
                   </p>
                 </div>

                 <div className="bg-indigo-50 border border-indigo-100 rounded-2xl p-4">
                   <MapPin className="text-indigo-600 mb-2" size={24} />
                   <h4 className="font-bold text-indigo-900 mb-1">Endereço</h4>
                   <p className="text-sm text-indigo-800">
                     O endereço completo pode aparecer em mensagens de confirmação de consulta e em documentos gerados pela plataforma.
                   </p>
                 </div>
               </div>

               <div className="mt-5 bg-amber-50 border border-amber-200 rounded-2xl p-4">
                 <p className="text-sm text-amber-800 mb-0">
                   <strong>Importante:</strong> o número usado pelo módulo WhatsApp Business Integrado é diferente da Extensão WhatsApp Web. A extensão abre o WhatsApp Web comum no navegador; o módulo integrado envia mensagens pela API oficial da Meta diretamente pelo OdontoAssist AI.
                 </p>
               </div>
            </div>

            <div className="bg-white p-8 rounded-3xl border border-gray-100 shadow-sm relative overflow-hidden">
               <div className="absolute top-0 left-0 w-2 h-full bg-indigo-500" />
               <h3 className="text-xl font-bold text-gray-900 mb-3 flex items-center gap-3">
                 <span className="w-8 h-8 rounded-full bg-indigo-100 text-indigo-700 flex items-center justify-center text-sm font-black">6</span>
                 Personalização do Menu Lateral
               </h3>

               <p>
                 Cada pessoa pode organizar o menu lateral do OdontoAssist AI de acordo com os módulos que realmente utiliza. Para fazer isso, acesse <strong>Configurações</strong> e abra a aba <strong>Menu</strong>.
               </p>

               <p>
                 Use os controles ao lado de cada módulo para escolher quais opções deseja manter visíveis. Depois, clique em <strong>Salvar preferências</strong>. O menu será atualizado imediatamente no computador, tablet e celular.
               </p>

               <div className="mt-5 bg-indigo-50 border border-indigo-100 rounded-2xl p-5 flex items-start gap-4">
                 <Layout className="text-indigo-600 shrink-0 mt-1" size={28} />
                 <div>
                   <h4 className="font-bold text-indigo-900 mb-1">Uma organização para cada usuário</h4>
                   <p className="text-sm text-indigo-800 mb-0">
                     A personalização é individual. Assim, o administrador, o dentista, a secretária e os demais integrantes da equipe podem manter menus diferentes, conforme suas atividades e permissões.
                   </p>
                 </div>
               </div>

               <div className="mt-5 bg-amber-50 border border-amber-200 rounded-2xl p-4">
                 <p className="text-sm text-amber-800 mb-0">
                   <strong>Fique tranquilo:</strong> ocultar um módulo não apaga informações, não desativa funcionalidades e não altera as permissões do usuário. A opção apenas deixa de aparecer no menu e pode ser restaurada a qualquer momento pelo botão <strong>Restaurar menu padrão</strong>.
                 </p>
               </div>
            </div>

          </div>

          <div className="bg-indigo-900 rounded-3xl p-8 mt-12 text-white">
             <h3 className="text-2xl font-bold mb-4">Atualizações em Tempo Real</h3>
             <ul className="space-y-3">
               <li className="flex items-start gap-3">
                 <CheckCircle2 className="text-indigo-400 shrink-0 mt-1" size={20} />
                 <span>Qualquer alteração feita no painel de Configurações entra em vigor imediatamente para todos os novos pacotes e prescrições.</span>
               </li>
               <li className="flex items-start gap-3">
                 <CheckCircle2 className="text-indigo-400 shrink-0 mt-1" size={20} />
                 <span>Dados como WhatsApp, telefone e endereço também serão aplicados nas novas mensagens automáticas enviadas pelo módulo WhatsApp Business.</span>
               </li>
               <li className="flex items-start gap-3">
                 <CheckCircle2 className="text-indigo-400 shrink-0 mt-1" size={20} />
                 <span>As preferências do menu ficam gravadas individualmente e permanecem aplicadas nos próximos acessos do usuário.</span>
               </li>               
             </ul>
          </div>
        </article>
      </main>
    </div>
  );
}
